function parseSlidePreviewUrlsRaw(raw: unknown): string[] {
  if (!raw) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((url) => (typeof url === 'string' ? url : ''));
  }
  if (typeof raw !== 'string') {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.map((url) => (typeof url === 'string' ? url : '')) : [];
  } catch {
    return [];
  }
}

/** @deprecated Prefer parseSlidePreviewUrlsRaw — kept for tests importing by name. */
export function parseSlidePreviewUrls(raw: string | undefined): string[] {
  return parseSlidePreviewUrlsRaw(raw);
}

/** Parse canonical imageUrls or slidePreviewUrls alias (JSON string or array). */
export function parseImageSequenceUrls(data: {
  imageUrls?: string | string[];
  slidePreviewUrls?: string | string[];
}): string[] {
  return parseSlidePreviewUrlsRaw(data.imageUrls ?? data.slidePreviewUrls);
}

export function buildPresentationDeckScope(itemId: string, assetVersion?: string): string {
  return assetVersion ? `${itemId}:v${assetVersion}` : itemId;
}

export function buildPresentationSlideCacheKey(
  itemId: string | undefined,
  assetVersion: string | undefined,
  slideIndex: number,
): string | undefined {
  if (!itemId) {
    return undefined;
  }
  return `${buildPresentationDeckScope(itemId, assetVersion)}:slide-${slideIndex}`;
}

/** Order slide indices closest to the current page first (for prioritized prefetch). */
export function orderSlideIndicesForPrefetch(slideCount: number, currentPage: number): number[] {
  if (slideCount <= 0) {
    return [];
  }
  const currentIndex = Math.max(0, Math.min(slideCount - 1, currentPage - 1));
  return Array.from({ length: slideCount }, (_, index) => index).sort((a, b) => {
    const distA = Math.abs(a - currentIndex);
    const distB = Math.abs(b - currentIndex);
    return distA - distB || a - b;
  });
}

export function clampPresentationPage(page: number, total: number): number {
  if (total <= 0) {
    return Math.max(1, page);
  }
  return Math.min(Math.max(1, page), total);
}

export type PresentationSlideControlAction = 'next' | 'prev' | 'first' | 'last' | 'goto';

/** Optimistic target page for live preview overlay (clamped to deck bounds). */
export function resolvePresentationSlideIndexAfterAction(
  action: PresentationSlideControlAction,
  currentPage: number,
  total: number,
  gotoPage?: number,
): number {
  if (total <= 0) {
    return Math.max(1, currentPage);
  }
  switch (action) {
    case 'next':
      return clampPresentationPage(currentPage + 1, total);
    case 'prev':
      return clampPresentationPage(currentPage - 1, total);
    case 'first':
      return 1;
    case 'last':
      return total;
    case 'goto':
      return clampPresentationPage(gotoPage ?? currentPage, total);
    default:
      return clampPresentationPage(currentPage, total);
  }
}

/** Same rule as Control slide-dialog next/prev: native PPT step unless Next look-ahead or backup stills. */
export function shouldUseNativeSlideStep(input: {
  canLiveSlideControl: boolean;
  preferAbsoluteSlideGoto: boolean;
  backupSlidesActive: boolean;
}): boolean {
  return input.canLiveSlideControl && !input.preferAbsoluteSlideGoto && !input.backupSlidesActive;
}

export function parseDwellMs(data: { dwellMs?: string }, defaultMs = 5000): number {
  if (data.dwellMs) {
    const parsed = Number.parseInt(data.dwellMs, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return defaultMs;
}
